import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import utils from '../../utils/utils';
import DATE_FORMAT from '../../components/constants/dateFormat';
import { FormattedMessage } from 'react-intl';
import { faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import apiCalls from '../../utils/apiCalls';

const AdminDeliveryDayRow = ({
  deliveryDay,
  showMarketplaces,
  refreshDeliveryDayMarketplace,
  setError,
  setLoading,
}) => {
  const [showDeliveryDayMarketplaces, setShowDeliveryDayMarketplaces] = useState(showMarketplaces);

  const deliveryDate = utils.getDateFromString(deliveryDay.deliveryDate).format(DATE_FORMAT.displayWithTimeAndWeekday);

  const marketplacesWithOrdersAndReceived = deliveryDay.marketplaces
    .filter((marketplace) => marketplace.totalSum > 0 && marketplace.dateReceived)
    .sort((a, b) => (a.totalSum > b.totalSum ? -1 : 1));

  const marketplacesWithOrdersAndNotReceived = deliveryDay.marketplaces
    .filter((marketplace) => marketplace.totalSum > 0 && !marketplace.dateReceived)
    .sort((a, b) => (a.totalSum > b.totalSum ? -1 : 1));
  const groupedMarketplacesWithOrdersNotReceived = utils.groupBy(
    marketplacesWithOrdersAndNotReceived,
    'marketplaceTransportType'
  );

  const marketplacesWithoutOrders = deliveryDay.marketplaces.filter((marketplace) => marketplace.totalSum === 0);

  const revenue = marketplacesWithOrdersAndReceived
    .concat(marketplacesWithOrdersAndNotReceived)
    .reduce((count, ddm) => {
      let rev = 0;
      if (ddm.billToUsId) {
        rev += ddm.totalSum >= 100 ? ddm.totalSum * 0.2 : ddm.totalSum * 0.15;
      } else {
        rev += ddm.totalSum * 0.2;
      }

      return rev + count;
    }, 0);

  function triggerDeliveryDayDateReceived(ddm) {
    setLoading(true);

    const params = ddm.dateReceived ? { resetReceived: true } : { markReceived: true };
    apiCalls
      .getDeliveryDayMarketplaces({
        deliveryDayMarketplaceId: ddm.id,
        ...params,
      })
      .then((result) => {
        refreshDeliveryDayMarketplace(result.data.deliveryDayMarketplaces[0]);
        setLoading(false);
      })
      .catch((errors) => {
        setError(utils.getErrorMessageFromResponse(errors));
        setLoading(false);
      });
  }

  const b2cTimeRanges = deliveryDay.timeRanges.filter((tr) => !tr.isBusinessToBusiness);
  const b2bTimeRanges = deliveryDay.timeRanges.filter((tr) => tr.isBusinessToBusiness);

  return (
    <tr>
      <td scope="row">
        <Link to={`/admin/deliveryDay/${deliveryDay.id}`} className="link">
          {deliveryDay.id}
        </Link>{' '}
        - {deliveryDate}
      </td>
      <td>{deliveryDay.totalSum}€</td>
      <td>
        <div className="m-b-1">
          <button
            className="btn btn-small btn-normal-link"
            onClick={() => setShowDeliveryDayMarketplaces(!showDeliveryDayMarketplaces)}
          >
            <small>
              <FontAwesomeIcon icon={showDeliveryDayMarketplaces ? faCaretUp : faCaretDown} /> Marketplaces
            </small>
          </button>
        </div>
        {showDeliveryDayMarketplaces ? (
          <>
            {Object.keys(groupedMarketplacesWithOrdersNotReceived).map((key, index) => {
              const ddms = groupedMarketplacesWithOrdersNotReceived[key];

              return (
                <div className="m-b-2" key={index}>
                  <h5 className="m-b-0">
                    {key !== 'null' ? (
                      <FormattedMessage id={`marketplace.form.field.transport.type.name.${key}`} />
                    ) : (
                      '-'
                    )}
                  </h5>
                  {ddms.map((ddm, index) => {
                    const receivedLink = ddm.dateReceived ? (
                      <small className="text-muted" onClick={() => triggerDeliveryDayDateReceived(ddm)}>
                        ✅
                      </small>
                    ) : (
                      <span className="text-muted" onClick={() => triggerDeliveryDayDateReceived(ddm)}>
                        ☑
                      </span>
                    );

                    return (
                      <div key={index}>
                        <Link to={`/admin/deliveryDayMarketplaces/${ddm.id}`}>
                          {ddm.marketplaceName} - {ddm.totalSum}€
                        </Link>{' '}
                        {receivedLink}
                      </div>
                    );
                  })}
                </div>
              );
            })}
            {marketplacesWithOrdersAndReceived.map((ddm, index) => {
              const receivedLink = ddm.dateReceived ? (
                <small className="text-muted" onClick={() => triggerDeliveryDayDateReceived(ddm)}>
                  ✅
                </small>
              ) : (
                <span className="text-muted" onClick={() => triggerDeliveryDayDateReceived(ddm)}>
                  ☑
                </span>
              );

              return (
                <div key={index}>
                  <Link to={`/admin/deliveryDayMarketplaces/${ddm.id}`}>
                    {ddm.marketplaceName} - {ddm.totalSum}€
                  </Link>{' '}
                  {receivedLink}
                </div>
              );
            })}
            <div className="m-b-1"></div>
            {marketplacesWithoutOrders.map((marketplace, index) => (
              <div key={index} className="text-muted">
                {marketplace.marketplaceName} - {marketplace.totalSum}€
              </div>
            ))}
          </>
        ) : (
          ''
        )}
        <div className="text-muted">
          <small>
            <FormattedMessage id={'admin.delivery.days.revenue'} />: {utils.getRoundedPrice(revenue)}€
          </small>
        </div>
      </td>
      <td>
        <div className="m-b-2">
          <div>
            <strong>B2C</strong>
          </div>
          {b2cTimeRanges.map((timeRange, index) => (
            <div key={index}>
              {utils.getFormattedTimeRange(timeRange)}, <small>#{timeRange.id}</small> - #{timeRange.serviceAreaId}{' '}
              {timeRange.serviceAreaName}
              {timeRange.remaining || timeRange.remaining === 0 ? `, Slots: ${timeRange.remaining}` : ''}{' '}
              {timeRange.reminderEmailsSent
                ? `, Emails ✅: ${utils.getFormattedDateFromString(timeRange.reminderEmailsSent, DATE_FORMAT.onlyTime)}`
                : ''}
            </div>
          ))}
        </div>
        <div>
          <div>
            <strong>B2B</strong>
          </div>
          {b2bTimeRanges.length
            ? b2bTimeRanges.map((timeRange, index) => (
                <div key={index}>
                  {utils.getFormattedTimeRange(timeRange)}, <small>#{timeRange.id}</small> - #{timeRange.serviceAreaId}{' '}
                  {timeRange.serviceAreaName}
                  {timeRange.remaining || timeRange.remaining === 0 ? `, Slots: ${timeRange.remaining}` : ''}
                  {timeRange.reminderEmailsSent
                    ? `, Emails ✅: ${utils.getFormattedDateFromString(
                        timeRange.reminderEmailsSent,
                        DATE_FORMAT.onlyTime
                      )}`
                    : ''}
                </div>
              ))
            : '-'}
        </div>
      </td>
      <td>
        <Link to={`/admin/deliveryDays/edit/${deliveryDay.id}`} className="link">
          Edit
        </Link>
      </td>
    </tr>
  );
};

AdminDeliveryDayRow.propTypes = {
  refreshDeliveryDayMarketplace: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  setLoading: PropTypes.func.isRequired,
  deliveryDay: PropTypes.object.isRequired,
  showMarketplaces: PropTypes.bool.isRequired,
};

AdminDeliveryDayRow.defaultProps = {};

export default AdminDeliveryDayRow;
